import { ref, type Ref } from 'vue';
import type { Geofence, ResolvedPlace } from '@gpx-spa/geo-domain';
import { geofenceToResolvedPlace } from '@gpx-spa/geo-domain';
import {
  applyAnchorManualSlotKeys,
  manualSlotKey,
  type FormPlannedSlot,
} from '@/components/shipment/form/utils/shipment-form-stop-time-autofill';
import { shipmentFormStopsToDeviceApiStops } from '@/components/shipment/form/utils/shipment-form-payload';
import {
  createEmptyStop,
  type ShipmentFormStopRow,
} from '@/stores/shipment-form/stop-row';
import type { ShipmentFormRouteComposerSlice } from '@/stores/shipment-form/route-composer-slice';

export function createShipmentFormStopsSlice(
  routeComposer: ShipmentFormRouteComposerSlice,
  hydration: {
    stopsSyncedOnDeviceApi: { value: boolean };
    resetHydrationFlags: () => void;
  },
  generalPlannedRange: Ref<[Date | null, Date | null] | null>,
) {
  const stops = ref<ShipmentFormStopRow[]>([
    createEmptyStop('origin'),
    createEmptyStop('destination'),
  ]);
  const manualPlannedSlots = ref(new Set<string>());
  const persistedStopsPayloadSnapshot = ref<string | null>(null);

  function stopsPayloadSnapshot(rows: ShipmentFormStopRow[]): string {
    return JSON.stringify(shipmentFormStopsToDeviceApiStops(rows));
  }

  function markStopsPayloadPersisted(rows: ShipmentFormStopRow[]) {
    persistedStopsPayloadSnapshot.value = stopsPayloadSnapshot(rows);
  }

  function haveStopsChangedSincePersisted(
    rows: ShipmentFormStopRow[],
  ): boolean {
    if (persistedStopsPayloadSnapshot.value == null) return true;
    return stopsPayloadSnapshot(rows) !== persistedStopsPayloadSnapshot.value;
  }

  function clearManualPlannedSlots() {
    manualPlannedSlots.value = new Set();
  }

  function markPlannedSlotManual(stopKey: string, slot: FormPlannedSlot) {
    const next = new Set(manualPlannedSlots.value);
    next.add(manualSlotKey(stopKey, slot));
    manualPlannedSlots.value = next;
  }

  function unmarkPlannedSlotManual(stopKey: string, slot: FormPlannedSlot) {
    const next = new Set(manualPlannedSlots.value);
    next.delete(manualSlotKey(stopKey, slot));
    manualPlannedSlots.value = next;
  }

  function markAnchorPlannedSlotsManual() {
    manualPlannedSlots.value = applyAnchorManualSlotKeys(
      manualPlannedSlots.value,
      stops.value,
      'mark',
    );
  }

  function unmarkAnchorPlannedSlotsManual() {
    manualPlannedSlots.value = applyAnchorManualSlotKeys(
      manualPlannedSlots.value,
      stops.value,
      'unmark',
    );
  }

  function resetStops() {
    stops.value = [createEmptyStop('origin'), createEmptyStop('destination')];
    persistedStopsPayloadSnapshot.value = null;
    routeComposer.persistedRouteComposerSnapshot.value = null;
    routeComposer.resetRouteComposerState();
    hydration.resetHydrationFlags();
    clearManualPlannedSlots();
  }

  function patchStop(key: string, patch: Partial<ShipmentFormStopRow>) {
    const i = stops.value.findIndex((s) => s.key === key);
    if (i === -1) return;
    stops.value[i] = { ...stops.value[i], ...patch };
  }

  function setStopResolved(key: string, place: ResolvedPlace | null) {
    patchStop(key, { resolved: place, geofence: null });
  }

  function setStopGeofence(key: string, gf: Geofence | null) {
    if (gf) {
      patchStop(key, { geofence: gf, resolved: geofenceToResolvedPlace(gf) });
    } else {
      patchStop(key, { geofence: null, resolved: null });
    }
  }

  function setStopRadius(key: string, radiusM: number) {
    patchStop(key, { radiusM });
  }

  function setStopPlannedArrival(key: string, d: Date | null) {
    patchStop(key, { plannedArrival: d });
  }

  function setStopPlannedDeparture(key: string, d: Date | null) {
    patchStop(key, { plannedDeparture: d });
  }

  function addWaypoint() {
    const insertAt = stops.value.length - 1;
    const row = createEmptyStop('waypoint');
    stops.value = [
      ...stops.value.slice(0, insertAt),
      row,
      ...stops.value.slice(insertAt),
    ];
    clearAllPlannedDates();
  }

  function removeStopAt(index: number) {
    if (stops.value.length <= 2) return;
    stops.value = stops.value.filter((_, i) => i !== index);
    clearAllPlannedDates();
  }

  function moveStop(index: number, delta: -1 | 1) {
    const j = index + delta;
    if (j < 0 || j >= stops.value.length) return;
    const next = [...stops.value];
    const t = next[index]!;
    next[index] = next[j]!;
    next[j] = t;
    stops.value = next;
    clearAllPlannedDates();
  }

  function applyPersistedStopIdsFromPosted(
    rows: { id: number; sequence: number }[],
  ) {
    for (const r of rows) {
      const i = r.sequence;
      if (i < 0 || i >= stops.value.length) continue;
      const row = stops.value[i];
      if (!row) continue;
      stops.value[i] = { ...row, persistedStopId: r.id };
    }
    hydration.stopsSyncedOnDeviceApi.value = true;
  }

  function clearAllPlannedDates() {
    for (const s of stops.value) {
      s.plannedArrival = null;
      s.plannedDeparture = null;
    }
    generalPlannedRange.value = null;
    clearManualPlannedSlots();
  }

  return {
    stops,
    manualPlannedSlots,
    persistedStopsPayloadSnapshot,
    markStopsPayloadPersisted,
    haveStopsChangedSincePersisted,
    resetStops,
    patchStop,
    setStopResolved,
    setStopGeofence,
    setStopRadius,
    setStopPlannedArrival,
    setStopPlannedDeparture,
    addWaypoint,
    removeStopAt,
    moveStop,
    applyPersistedStopIdsFromPosted,
    clearAllPlannedDates,
    markPlannedSlotManual,
    unmarkPlannedSlotManual,
    markAnchorPlannedSlotsManual,
    unmarkAnchorPlannedSlotsManual,
    clearManualPlannedSlots,
  };
}
